/**
 * workspace-url — the `/w/<slug>` prefix that puts the workspace in the URL.
 *
 * WHY THIS EXISTS: every peer CRM identifies the workspace in the address bar —
 * Salesforce and Pipedrive in the subdomain, HubSpot/Attio/Linear in the first
 * path segment. xCRMiQ had nothing: the tenant came only from the JWT
 * (middleware/tenant-context.ts reads it from the token, never from the host or
 * path). Three concrete costs of that:
 *
 *   1. A pasted link is ambiguous for anyone who belongs to two workspaces —
 *      the same URL shows different data depending on who is signed in.
 *   2. You cannot have two workspaces open in two tabs; the second login
 *      silently changes what the first tab is looking at.
 *   3. The URL cannot tell you whose data is on screen — bad in a screenshot,
 *      a support ticket, or a shared bookmark.
 *
 * `/w/<slug>` was chosen over a bare `/<slug>` first segment because the router
 * already serves CMS pages from a `:slug` catch-all and static top-level routes
 * (/login, /pricing, /privacy, …). A bare workspace segment would be ambiguous
 * with all of them, and a tenant whose slug happened to be "leads" or "login"
 * would break routing outright. `/w/` is a reserved namespace: unambiguous, no
 * denylist to keep in sync.
 *
 * MECHANISM: the prefix is applied as the react-router `basename`, NOT written
 * into the 141 `navigate('/leads')` / `to="/leads"` call sites across 68 files.
 * React Router adds the basename on the way out and strips it on the way in, so
 * every existing path literal keeps working untouched and there is exactly one
 * place that knows about `/w`. The trade-off is that the basename is fixed for
 * the life of the router instance — changing workspace needs a real navigation,
 * which is what `workspaceHref` + `window.location.replace` are for below.
 */

import { APP_ROOT_SEGMENTS } from '../routes/paths'

/**
 * The shape of a routable workspace slug — stated ONCE and reused by both the
 * matcher and the guard below, so the two can never disagree about what a slug
 * is. Mirrors exactly what `backend/src/lib/slug.ts:slugify()` guarantees:
 * lowercase ASCII, hyphen-separated, no leading/trailing/double hyphen.
 */
const SLUG_PATTERN = '[a-z0-9](?:[a-z0-9-]*[a-z0-9])?'

/** `/w/acme` — matched at the very start of a pathname. */
const WORKSPACE_RE = new RegExp(`^/w/(${SLUG_PATTERN})(?=/|$)`, 'i')

/** Whole-string form of the same pattern. */
const ROUTABLE_SLUG_RE = new RegExp(`^${SLUG_PATTERN}$`, 'i')

/**
 * Can this slug survive a round-trip through the URL?
 *
 * Load-bearing, not cosmetic. `readWorkspaceBasename` runs at boot and only
 * recognises slugs matching the pattern above. If we ever wrote a slug it
 * cannot match into a URL, the basename would come back '', the router would
 * fail to match the prefixed path, the catch-all would fire, and the sync
 * effect would redirect again — an infinite reload that takes the whole app
 * down for that tenant.
 *
 * Today `slugify()` is the only producer of tenant slugs and always satisfies
 * this, so the guard is unreachable. It exists because the cost of being wrong
 * is a hard outage and the cost of the guard is one regex: a hand-edited slug
 * or a future admin-side create endpoint (which validates only
 * `z.string().min(1).max(64)` for OTHER slug columns today) would otherwise
 * brick sign-in with no obvious cause.
 */
export function isRoutableWorkspaceSlug(slug: string | null | undefined): slug is string {
  return typeof slug === 'string' && ROUTABLE_SLUG_RE.test(slug)
}

/**
 * The router basename for the CURRENT location: `/w/<slug>` when the URL is
 * workspace-scoped, `''` otherwise (public pages, and legacy un-prefixed app
 * links that have not been upgraded yet).
 *
 * Called once at boot, before the router mounts.
 */
export function readWorkspaceBasename(pathname: string): string {
  const m = WORKSPACE_RE.exec(pathname)
  return m ? `/w/${m[1]}` : ''
}

/** The workspace slug in the current URL, or null when there isn't one. */
export function readWorkspaceSlug(pathname: string): string | null {
  const m = WORKSPACE_RE.exec(pathname)
  return m ? (m[1] as string).toLowerCase() : null
}

/**
 * Absolute in-app href for a path under a given workspace.
 * `path` is the un-prefixed app path (`/leads/lead_7Kq2mXf9Lp`).
 */
export function workspaceHref(slug: string, path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`
  return `/w/${slug}${clean === '/' ? '' : clean}`
}


/* ───────────────────────────── the redirect decision ────────────────────── */

/**
 * What (if anything) the app should do about the current URL once the signed-in
 * workspace is known.
 *
 *   'none'     — the URL is already correct, or is none of our business
 *   'prefix'   — a legacy un-prefixed app URL, upgrade it in place
 *   'mismatch' — the link names a DIFFERENT workspace than the session
 */
export type WorkspaceRedirect =
  | { kind: 'none' }
  | { kind: 'prefix'; url: string }
  | { kind: 'mismatch'; url: string }

const NO_REDIRECT: WorkspaceRedirect = { kind: 'none' }

/**
 * Pure decision behind WorkspaceUrlSync (routes/router.tsx).
 *
 * Extracted from the component deliberately: the component can only express
 * this as a `window.location.replace` side effect, which is close to untestable
 * in jsdom, and getting it wrong means an infinite redirect loop rather than a
 * wrong pixel. As a function the whole decision table is exhaustively
 * exercisable — see __tests__/workspace-url.test.ts.
 *
 * Takes the BROWSER location, not the router's: react-router has already
 * stripped the basename from `useLocation().pathname`, and the whole question
 * here is whether the real URL carries the prefix.
 */
export function resolveWorkspaceRedirect(input: {
  pathname: string
  search: string
  hash: string
  sessionSlug: string | null | undefined
}): WorkspaceRedirect {
  const { pathname, search, hash, sessionSlug } = input

  // No workspace on the session (super-admin, or /auth/me still in flight):
  // there is nothing to prefix WITH, so leave the URL alone.
  if (!sessionSlug) return NO_REDIRECT
  // A slug that cannot round-trip would loop forever — see isRoutableWorkspaceSlug.
  if (!isRoutableWorkspaceSlug(sessionSlug)) return NO_REDIRECT

  const urlSlug = readWorkspaceSlug(pathname)

  if (urlSlug === null) {
    const root = pathname.split('/')[1] ?? ''
    // `/` is the app root and belongs under the workspace. Anything else must
    // be a known app segment, or we leave it alone — the router also serves
    // single-segment CMS pages from a `:slug` catch-all, and `/about-us` is
    // not ours to rewrite.
    if (root !== '' && !APP_ROOT_SEGMENTS.has(root)) return NO_REDIRECT
    return { kind: 'prefix', url: `${workspaceHref(sessionSlug, pathname)}${search}${hash}` }
  }

  if (urlSlug !== sessionSlug.toLowerCase()) {
    // Do NOT rewrite to the session's workspace: that would show this user a
    // different workspace's data at a URL naming someone else's, which is
    // precisely the ambiguity the prefix exists to remove. Make them choose,
    // and carry the original path so they land where the link pointed.
    const next = encodeURIComponent(`${pathname}${search}${hash}`)
    return { kind: 'mismatch', url: `/login?workspace=${encodeURIComponent(urlSlug)}&next=${next}` }
  }

  return NO_REDIRECT
}
